import React, { useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatMoneda } from "./utils";

const TITULO_OTROS_IMPUESTOS = "Otros impuestos en factura (informativo)";

const ImpuestosFacturaPanel = ({
  impuestos = [],
  totales = null,
  compact = false,
  titulo = TITULO_OTROS_IMPUESTOS,
}) => {
  const totalesCalculados = useMemo(() => {
    if (totales?.total_factura != null || totales?.total_aportado != null) {
      return totales;
    }
    if (impuestos.length <= 1) return null;
    return impuestos.reduce(
      (acc, item) => ({
        total_factura:
          acc.total_factura + (Number(item.importe_factura) || 0),
        total_aportado:
          acc.total_aportado + (Number(item.importe_aportado) || 0),
      }),
      { total_factura: 0, total_aportado: 0 }
    );
  }, [impuestos, totales]);

  const mostrarTotales =
    impuestos.length > 1 &&
    (totalesCalculados?.total_factura != null ||
      totalesCalculados?.total_aportado != null);

  if (!Array.isArray(impuestos) || impuestos.length === 0) return null;

  return (
    <Box
      sx={{
        p: compact ? 0 : 1.5,
        backgroundColor: compact ? "transparent" : "#f8fafc",
        border: compact ? "none" : "1px dashed #c5d4e3",
        borderRadius: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 1 }}>
        <InfoOutlinedIcon
          fontSize="small"
          color="info"
          sx={{ mt: compact ? 0.1 : 0.25 }}
        />
        <Box>
          <Typography
            variant={compact ? "subtitle2" : "subtitle1"}
            fontWeight={600}
            color="text.secondary"
          >
            {titulo}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            No se restan del total a pagar
          </Typography>
        </Box>
      </Box>

      <TableContainer>
        <Table size="small" sx={{ minWidth: 280 }}>
          <TableHead>
            <TableRow>
              {["Impuesto", "En factura", "En este pago"].map((h) => (
                <TableCell
                  key={h}
                  align={h === "Impuesto" ? "left" : "right"}
                  sx={{
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: 12,
                    color: "text.secondary",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {impuestos.map((item, index) => (
              <TableRow key={`${item.descripcion ?? "imp"}-${index}`}>
                <TableCell sx={{ py: 0.75, borderBottom: "1px solid #eee" }}>
                  <Typography variant="body2" fontWeight={500}>
                    {item.descripcion || "Impuesto"}
                  </Typography>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ py: 0.75, borderBottom: "1px solid #eee", whiteSpace: "nowrap" }}
                >
                  {formatMoneda(item.importe_factura)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    py: 0.75,
                    borderBottom: "1px solid #eee",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatMoneda(item.importe_aportado)}
                </TableCell>
              </TableRow>
            ))}
            {mostrarTotales ? (
              <TableRow>
                <TableCell sx={{ py: 0.75, fontWeight: 600 }}>Total</TableCell>
                <TableCell align="right" sx={{ py: 0.75, fontWeight: 600 }}>
                  {formatMoneda(totalesCalculados.total_factura)}
                </TableCell>
                <TableCell align="right" sx={{ py: 0.75, fontWeight: 600 }}>
                  {formatMoneda(totalesCalculados.total_aportado)}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ImpuestosFacturaPanel;
